import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

// Services
import { RequestsService } from 'src/app/core/services/http/requests.service';
import { RefreshService } from 'src/app/refresh.service';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { ApiService } from 'src/app/services/api.service';

// Constants
import { Urls } from 'src/app/lib/urls';
import { TRANSACTION_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { PAYBILLS_TABLE_SUB_HEADERS } from 'src/app/lib/constants/transactions.constants';

@Component({
  selector: 'app-pay-bills',
  templateUrl: './pay-bills.component.html',
  styleUrls: ['./pay-bills.component.scss']
})
export class PayBillsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  testValue = 2.668888999;
  tabs = TRANSACTION_TAB_OPTIONS;
  public form: UntypedFormGroup;
  searchForm: UntypedFormGroup;
  limitForm: UntypedFormGroup;

  // Dropdown options
  categoryOptions = [
    { label: 'Telecom',           value: '2' },
    { label: 'Credit Card',       value: '3' },
    { label: 'Cable and Internet', value: '4' },
    { label: 'Government',        value: '5' },
    { label: 'Loans',             value: '6' },
    { label: 'Others',            value: '7' },
    { label: 'Travel',            value: '8' },
    { label: 'Transpo',           value: '9' },
    { label: 'Shopping',          value: '10' },
    { label: 'Power',             value: '11' },
    { label: 'Water',             value: '12' },
  ];
  statusOptions = [
    { label: 'Success', value: '1' },
    { label: 'Failed',  value: '3' },
  ];

  categoryType: any = null;
  categoryType_text: any = null;
  statusType: any = null;
  statusType_text: any = null;

  // Table
  tableSubHeader = PAYBILLS_TABLE_SUB_HEADERS;

  // Data
  itemLists: any[] = [];
  submitted = false;
  isLoading = false;
  showTable = true;
  fileName = '';

  // Filter fields — stores submitted criteria for pagination/search/sort reuse
  filterFields: any = null;

  // Pagination
  size = 10;
  total_pages = 0;
  current_page = 1;
  pages_array: number[] = [];
  total = 0;

  // Search & limit
  search_keyword = '';
  searchKey: any = null;
  limitKey: any = null;

  // Sorting
  sort_column = 'dateTime';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  // ─── Reset Helpers ───────────────────────────────────────────────────────────

  private clearSort(): void {
    this.sort_column = 'dateTime';
    this.sort_type   = 'ASC';
  }


  private clearForms(): void {
    this.form.reset();
    this.searchForm.reset({ search: '' });
    this.limitForm.reset({ limit: 10 });
  }

  private clearDropdowns(): void {
    this.categoryType = null;
    this.categoryType_text = null;
    this.statusType = null;
    this.statusType_text = null;
  }

  private clearData(): void {
    this.itemLists = [];
    this.submitted = false;
    this.isLoading = false;
    this.fileName = '';
  }

  private clearFilterFields(): void {
    this.filterFields = null;
  }

  private clearPagination(): void {
    this.size = 10;
    this.total_pages = 0;
    this.current_page = 1;
    this.pages_array = [];
    this.total = 0;
  }

  private clearSearch(): void {
    this.search_keyword = '';
  }

  private clearLocalStorage(): void {
    localStorage.setItem('itemLists', JSON.stringify(null));
  }

  private clearComponentState(): void {
    this.clearForms();
    this.clearDropdowns();
    this.clearData();
    this.clearFilterFields();
    this.clearPagination();
    this.clearSearch();
    this.clearLocalStorage();
    this.clearSort()
  }

  // ─── Constructor & Lifecycle ─────────────────────────────────────────────────

  constructor(
    private fb: UntypedFormBuilder,
    private request: RequestsService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    public apiService: ApiService,
    public debounce: DebounceService,
    public paginationHelper: PaginationHelperService,
    private refreshService: RefreshService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.spinner.hide().catch((err) => {
      console.error('Spinner hide error on init:', err);
    });

    this.refreshService.resetTable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.clearComponentState();
      this.showTable = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.spinner.hide().catch((err) => {
      console.error('Spinner hide error on destroy:', err);
    });
  }

  // ─── Form Initialization ─────────────────────────────────────────────────────

  initializeForm(): void {
    this.form = this.fb.group({
      transactionId: [null],
      phoneNumber:   [null],
      billerName:    [null],
      firstName:     [null],
      middleName:    [null],
      lastName:      [null],
      dateFrom:      [null],
      dateTo:        [null],
    });

    // Search with debounce
    this.searchKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.search_keyword = this.searchForm.value.search;

      if (this.filterFields) {
        this.get_filtered_list();
      }
    }, 500);

    this.searchForm = this.fb.group({ search: [''] });

    // Limit with debounce
    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size = this.limitForm.value.limit;

      if (this.filterFields) {
        this.get_filtered_list();
      }
    }, 500);

    this.limitForm = this.fb.group({ limit: [this.size] });
  }

  // ─── Submit & Fetch ──────────────────────────────────────────────────────────

  submit(): void {
    if (!this.validateDates()) {
      return;
    }

    this.isLoading = true;
    this.clearSort();
    this.spinner.show().catch((err) => {
      console.error('Spinner show error:', err);
    });

    this.current_page = 1;

    const params = {
      transactionId: this.form.controls.transactionId.value,
      phoneNumber:   this.form.controls.phoneNumber.value,
      billerName:    this.form.controls.billerName.value,
      firstName:     this.form.controls.firstName.value,
      middleName:    this.form.controls.middleName.value,
      lastName:      this.form.controls.lastName.value,
      dateFrom:      this.form.controls.dateFrom.value,
      dateTo:        this.form.controls.dateTo.value,
      category:      this.categoryType,
      status:        this.statusType,
      sortBy: {
        [this.sort_column]: this.sort_type,
      },
    };

    this.filterFields = params;
    localStorage.setItem('itemLists', JSON.stringify(params));
    this.pages_array = [];

    this.apiService.getPayBillsTransactions(params, this.current_page, this.size)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            this.itemLists = response.result.data;
            this.submitted = true;

            this.pages_array = this.paginationHelper.generatePageArray(
              this.current_page,
              response.result.totalPages
            );
            this.total_pages  = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total        = parseInt(response.result.totalItems.toString(), 10);
          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error}`,
            'Failed Fetching Pay Bills Transactions'
          );
        },
        complete: () => {
          // Clear filter form after submission (keep filterFields for reuse)
          this.form.reset();
          this.clearDropdowns();
          
          this.isLoading = false;
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
        },
      });

    this.showTable = true;
  }

  get_filtered_list(): void {
    this.isLoading = true;

    const params = {
      ...this.filterFields,
      search: this.search_keyword,
      sortBy: {
        [this.sort_column]: this.sort_type,
      },
    };

    localStorage.setItem('itemLists', JSON.stringify(params));
    this.pages_array = [];

    this.apiService.getPayBillsTransactions(params, this.current_page, this.size)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            this.itemLists = response.result.data;

            this.pages_array = this.paginationHelper.generatePageArray(
              this.current_page,
              response.result.totalPages
            );
            this.total_pages  = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total        = parseInt(response.result.totalItems.toString(), 10);
          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error}`,
            'Failed Fetching Pay Bills Transactions'
          );
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
        },
      });
  }

  // ─── Dropdown Setters ────────────────────────────────────────────────────────

  setCategory(text: string, value: string): void {
    this.categoryType      = value;
    this.categoryType_text = text;
  }

  setStatus(text: string, value: string): void {
    this.statusType      = value;
    this.statusType_text = text;
  }

  // ─── Table Controls ──────────────────────────────────────────────────────────

  onLimitChangeFromComponent(event: any): void {
    this.limitForm.patchValue({ limit: event.target.value });
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.keyCode === 13) {
      this.searchList();
    }
  }

  searchList(): void {
    this.current_page    = 1;
    this.search_keyword  = this.searchForm.value.search;

    if (this.filterFields) {
      this.get_filtered_list();
    }
  }

  // ─── Sort ────────────────────────────────────────────────────────────────────

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    this.spinner.show().catch((err) => {
      console.error('Spinner show sort error:', err);
    });

    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column,
      this.sort_type,
      sort_column,
      sort_type
    );

    this.sort_column = column;
    this.sort_type   = type;

    if (this.submitted && this.filterFields) {
      this.get_filtered_list();
    }
  }

  // ─── Pagination ──────────────────────────────────────────────────────────────

  setPage(num: number): void {
    this.current_page = num;
    if (this.filterFields) {
      this.get_filtered_list();
    }
  }

  nextPage(): void {
    if (this.current_page < this.total_pages) {
      this.current_page++;
      if (this.filterFields) {
      this.get_filtered_list();
    }
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }
  }

  // ─── Validation ──────────────────────────────────────────────────────────────

  validateDates(): boolean {
    const dateFrom = this.form.get('dateFrom').value;
    const dateTo   = this.form.get('dateTo').value;

    if (dateFrom && dateTo) {
      const from = new Date(dateFrom);
      const to   = new Date(dateTo);
      from.setHours(0, 0, 0, 0);
      to.setHours(0, 0, 0, 0);

      if (to < from) {
        this.toastr.error('Date To cannot be earlier than Date From', 'Invalid Date Range');
        return false;
      }
    }
    return true;
  }

  // ─── Export ──────────────────────────────────────────────────────────────────

  async print(): Promise<void> {
    this.spinner.show().catch((error) => { console.log(error); });
    const current = new Date();
    this.filterFields = JSON.parse(localStorage.getItem('itemLists'));
    this.fileName = current.toLocaleDateString() + '-pay-bills.xlsx';

    try {
      await this.request.postBlob(Urls.exportTransactionsPaybills, this.filterFields).then((report) => {
        const blob = new Blob([report], { type: 'application/vnd.ms-excel' });
        const data = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = data;
        link.download = this.fileName;
        link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      });
      this.spinner.hide().catch((error) => { console.log(error); });
    } catch (error) {
      this.spinner.hide().catch((err) => { console.log(err); });
    }
  }
}
